import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  MenuBook,
  Visibility,
  People,
  RecordVoiceOver,
  Gavel,
  Description,
  Receipt,
  Info,
  Assignment,
  AccountBalance,
  AccountBalanceWallet,
  Build,
  LocalHospital,
  School,
  SupervisorAccount,
  Group,
  LocalPharmacy,
  HourglassEmpty,
} from "@material-ui/icons";
import {
  EasyNavigationCard,
  EasyNavigationContainer,
  EasyNavigationGroup,
  EasyNavigationGroupLabel,
} from "./easyNavigation.styles";
import { Section, SectionTitle } from "components/section";
import { useAppState } from "main/context/app-context";

export const EasyNavigationSection = () => {
  const { applicationData } = useAppState();
  const navigate = useNavigate();

  const isExternalUrl = (url: string): boolean => {
    return url.startsWith("http://") || url.startsWith("https://");
  };

  const handleNavigation = (route: string) => {
    if (isExternalUrl(route)) {
      window.open(route, "_blank");
    } else {
      navigate(route);
    }
  };

  const generalItems = useMemo(() => {
    const items = [];
    const serviceUrls = applicationData?.entity?.serviceUrls;

    if (serviceUrls?.urlDiarioOficial) {
      items.push({
        label: "DIÁRIO OFICIAL",
        Icon: MenuBook,
        route: serviceUrls.urlDiarioOficial,
      });
    } else {
      items.push({
        label: "DIÁRIO OFICIAL",
        Icon: MenuBook,
        route: "/publicacoes/diario-oficial",
      });
    }

    if (serviceUrls?.urlTransparencia) {
      items.push({
        label: "TRANSPARÊNCIA",
        Icon: Visibility,
        route: serviceUrls.urlTransparencia,
      });
    }

    if (serviceUrls?.urlServidores) {
      items.push({
        label: "SERVIDORES",
        Icon: People,
        route: serviceUrls.urlServidores,
      });
    }

    if (serviceUrls?.urlOuvidoria) {
      items.push({
        label: "OUVIDORIA",
        Icon: RecordVoiceOver,
        route: serviceUrls.urlOuvidoria,
      });
    }

    if (serviceUrls?.urlNotaFiscal) {
      items.push({
        label: "NOTA FISCAL",
        Icon: Receipt,
        route: serviceUrls.urlNotaFiscal,
      });
    }

    if (serviceUrls?.urlSic) {
      items.push({
        label: "E-SIC",
        Icon: Info,
        route: serviceUrls.urlSic,
      });
    }

    return items;
  }, [applicationData?.entity?.serviceUrls]);

  // Páginas de transparência (PNTP): sempre em sua própria linha, abaixo dos itens gerais acima.
  // A Câmara Municipal não tem Obras/Convênios/Saúde/Educação/Assistência Social (funções do Executivo),
  // então recebe um recorte próprio em vez da lista pensada para a Prefeitura.
  const isCamara = applicationData?.entity?.previousName === "CAMARA MUNICIPAL";

  const transparencyItems = useMemo(() => {
    const serviceUrls = applicationData?.entity?.serviceUrls;

    if (isCamara) {
      return [
        { label: "LICITAÇÕES", Icon: Gavel, route: serviceUrls?.urlLicitacoes || "/licitacoes" },
        { label: "CONTRATOS", Icon: Description, route: serviceUrls?.urlContratos || "/contratos" },
        { label: "PRESTAÇÃO DE CONTAS", Icon: AccountBalanceWallet, route: "/prestacao-contas" },
        { label: "MESA DIRETORA", Icon: Group, route: "/mesa-diretora" },
        { label: "VEREADORES", Icon: Group, route: "/vereadores" },
        { label: "SESSÕES LEGISLATIVAS", Icon: Assignment, route: "/sessoes-legislativas" },
        { label: "PRESENÇA EM SESSÕES", Icon: Assignment, route: "/presencas-sessao" },
        { label: "PROJETOS DE LEI", Icon: Description, route: "/projetos-lei" },
        { label: "ATOS NORMATIVOS", Icon: Gavel, route: "/atos-normativos" },
        { label: "VOTAÇÕES NOMINAIS", Icon: Assignment, route: "/votacoes-nominais" },
        { label: "COTA PARLAMENTAR", Icon: AccountBalanceWallet, route: "/cotas-parlamentares" },
        { label: "ATIVIDADE PARLAMENTAR", Icon: Assignment, route: "/atividades-parlamentares" },
        { label: "COMISSÕES", Icon: Group, route: "/comissoes" },
        { label: "PRESTAÇÃO DE CONTAS DO EXECUTIVO", Icon: AccountBalanceWallet, route: "/prestacoes-conta-executivo" },
      ];
    }

    return [
      { label: "LICITAÇÕES", Icon: Gavel, route: serviceUrls?.urlLicitacoes || "/licitacoes" },
      { label: "CONVÊNIOS", Icon: Assignment, route: "/convenios" },
      { label: "EMENDAS PARLAMENTARES", Icon: AccountBalance, route: "/emendas-parlamentares" },
      { label: "CONTRATOS", Icon: Description, route: serviceUrls?.urlContratos || "/contratos" },
      { label: "OBRAS", Icon: Build, route: "/obras" },
      { label: "PRESTAÇÃO DE CONTAS", Icon: AccountBalanceWallet, route: "/prestacao-contas" },
      { label: "SAÚDE", Icon: LocalHospital, route: "/documentos-setoriais/area/saude" },
      { label: "UNIDADES DE SAÚDE", Icon: LocalHospital, route: "/unidades-saude" },
      { label: "ESTOQUE DE MEDICAMENTOS", Icon: LocalPharmacy, route: "/estoques-medicamento" },
      { label: "CRECHES - LISTA DE ESPERA", Icon: HourglassEmpty, route: "/filas-espera?tipo=1" },
      { label: "FILAS DE ESPERA (SAÚDE)", Icon: HourglassEmpty, route: "/filas-espera" },
      { label: "PLANO DE CONTRATAÇÕES ANUAL", Icon: Assignment, route: "/planos-contratacao-anual" },
      { label: "EDUCAÇÃO", Icon: School, route: "/documentos-setoriais/area/educacao" },
      { label: "ASSISTÊNCIA SOCIAL", Icon: SupervisorAccount, route: "/documentos-setoriais/area/assistencia-social" },
    ];
  }, [applicationData?.entity?.serviceUrls, isCamara]);

  if (generalItems.length === 0 && transparencyItems.length === 0) {
    return null;
  }

  const renderCards = (items: typeof generalItems) =>
    items.map((item) => {
      const IconComponent = item.Icon;
      return (
        <EasyNavigationCard
          onClick={() => handleNavigation(item.route)}
          key={item.label}
        >
          <IconComponent style={{ fontSize: 48, color: 'inherit' }} />
          <span>{item.label}</span>
        </EasyNavigationCard>
      );
    });

  return (
    <Section background="gray">
      <SectionTitle>Acesso rápido</SectionTitle>
      {generalItems.length > 0 && (
        <EasyNavigationGroup>
          <EasyNavigationContainer>
            {renderCards(generalItems)}
          </EasyNavigationContainer>
        </EasyNavigationGroup>
      )}
      <EasyNavigationGroup>
        <EasyNavigationGroupLabel>Transparência Pública</EasyNavigationGroupLabel>
        <EasyNavigationContainer>
          {renderCards(transparencyItems)}
        </EasyNavigationContainer>
      </EasyNavigationGroup>
    </Section>
  );
};
